package dev.kotlinfest._2026.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.dom.ref
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.components.overlay.OverlayVars
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import dev.kotlinfest._2026.SiteLayout
import dev.kotlinfest._2026.SiteLinks
import dev.kotlinfest._2026.SitePalette
import dev.kotlinfest._2026.components.widgets.*
import dev.kotlinfest._2026.sitePageBackground
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent

val NavHeaderStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().height(80.px).padding(leftRight = SiteLayout.MobileMarginPx.px)
            .sitePageBackground()
            .position(Position.Fixed)
            .top(0.px)
            .left(0.px)
            .zIndex(10)
    }
    Breakpoint.MD {
        Modifier.height(160.px).padding(leftRight = 40.px)
    }
}

val HeaderLogoStyle = CssStyle {
    base { Modifier.width(222.px).height(56.px) }
    Breakpoint.MD { Modifier.width(317.px).height(80.px) }
}

val DesktopMenuStyle = CssStyle {
    base { Modifier.styleModifier { property("gap", "clamp(12px, 2.22vw, 32px)") } }
}

val MobileIconButtonStyle = CssStyle {
    base {
        Modifier.size(32.px).padding(0.px).backgroundColor(Colors.Transparent).styleModifier {
            property("appearance", "none")
            property("border", "0")
            property("box-shadow", "none")
            property("min-height", "0")
            property("min-width", "0")
        }
    }
}

val MobileMenuContentStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().padding(top = 64.px, bottom = 48.px).gap(64.px)
    }
}

val MobilePrimaryMenuStyle = CssStyle {
    base {
        Modifier.gap(64.px)
    }
}

val MobilePolicyMenuStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().padding(leftRight = 24.px).gap(24.px).styleModifier {
            property("flex-wrap", "wrap")
            property("justify-content", "flex-start")
        }
    }
}

@Composable
private fun HeaderLogo() {
    Link("/", variant = UndecoratedLinkVariant.then(UncoloredLinkVariant)) {
        Image(
            "/svg/Kotlinfest2026_logo-data_3-1.svg",
            "Kotlin Fest 2026",
            HeaderLogoStyle.toModifier().display(DisplayStyle.Block),
        )
    }
}

@Composable
private fun DesktopMenu() {
    Row(
        DesktopMenuStyle.toModifier().displayIfAtLeast(Breakpoint.LG),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        TextButton("Home", "/", language = SiteButtonLanguage.EN)
        TextButton("Timetable", "/timetable", language = SiteButtonLanguage.EN)
        TextButton("Sponsors", SiteLinks.Sponsors, language = SiteButtonLanguage.EN)
        SiteButton("Ticket", "/ticket", language = SiteButtonLanguage.EN)
    }
}

@Composable
private fun MobileMenuIcon() {
    Box(
        Modifier.size(32.px),
        contentAlignment = Alignment.Center,
    ) {
        Image("/img/menu-ring.svg", "", Modifier.width(29.333.px).height(29.333.px))
        Image("/img/menu.svg", "", Modifier.width(16.px).height(11.px))
    }
}

enum class SideMenuState { CLOSED, OPEN }

private const val MobileMenuDialogId = "mobile-menu-dialog"

private fun trapFocusInsideMenu(event: KeyboardEvent) {
    val dialog = document.getElementById(MobileMenuDialogId) as? HTMLElement ?: return
    val focusableElements = dialog.querySelectorAll("a[href], button:not([disabled])")
    val first = focusableElements.item(0) as? HTMLElement ?: return
    val last = focusableElements.item(focusableElements.length - 1) as? HTMLElement ?: return
    val active = document.activeElement
    when {
        event.shiftKey && active === first -> {
            event.preventDefault()
            last.focus()
        }
        !event.shiftKey && active === last -> {
            event.preventDefault()
            first.focus()
        }
    }
}

@Composable
fun NavHeader(modifier: Modifier = Modifier) {
    var menuState by remember { mutableStateOf(SideMenuState.CLOSED) }
    val closeMenu = { menuState = SideMenuState.CLOSED }

    Row(NavHeaderStyle.toModifier().then(modifier), verticalAlignment = Alignment.CenterVertically) {
        HeaderLogo()
        Spacer()
        DesktopMenu()
        Row(
            Modifier.displayUntil(Breakpoint.LG).then(
                if (menuState == SideMenuState.CLOSED) {
                    Modifier
                } else {
                    Modifier.styleModifier {
                        property("pointer-events", "none")
                        property("visibility", "hidden")
                    }
                },
            ),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            com.varabyte.kobweb.silk.components.forms.Button(
                onClick = { menuState = SideMenuState.OPEN },
                modifier = MobileIconButtonStyle.toModifier().ariaLabel("メニューを開く"),
            ) {
                MobileMenuIcon()
            }
        }
    }

    if (menuState == SideMenuState.OPEN) {
        MobileMenu(closeMenu)
    }
}

@Composable
private fun MobileMenu(close: () -> Unit) {
    val previouslyFocused = remember { document.activeElement as? HTMLElement }
    DisposableEffect(Unit) {
        val oldOverflow = document.body?.style?.getPropertyValue("overflow") ?: ""
        document.body?.style?.setProperty("overflow", "hidden")
        val listener = EventListener { event ->
            val keyboardEvent = event as? KeyboardEvent
            if (keyboardEvent != null) {
                when (keyboardEvent.key) {
                    "Escape" -> close()
                    "Tab" -> trapFocusInsideMenu(keyboardEvent)
                }
            }
        }
        val linkClickListener = EventListener { event ->
            val target = event.target as? Element
            if (target?.closest("#$MobileMenuDialogId a[href]") != null) close()
        }
        window.addEventListener("keydown", listener)
        // Kobweb's Link handles the click before it bubbles up to document, so listen during capture.
        document.addEventListener("click", linkClickListener, true)
        onDispose {
            window.removeEventListener("keydown", listener)
            document.removeEventListener("click", linkClickListener, true)
            document.body?.style?.setProperty("overflow", oldOverflow)
            previouslyFocused?.focus()
        }
    }

    Overlay(
        Modifier.setVariable(OverlayVars.BackgroundColor, SitePalette.brand.white)
            .attr("id", MobileMenuDialogId)
            .role("dialog")
            .attr("aria-modal", "true")
            .ariaLabel("メニュー")
            .styleModifier { property("z-index", "1000") },
    ) {
        Column(
            Modifier.fillMaxSize().padding(leftRight = SiteLayout.MobileMarginPx.px)
                .backgroundColor(SitePalette.brand.white).styleModifier { property("overflow-y", "auto") },
        ) {
            Row(Modifier.fillMaxWidth().height(80.px), verticalAlignment = Alignment.CenterVertically) {
                HeaderLogo()
                Spacer()
                com.varabyte.kobweb.silk.components.forms.Button(
                    onClick = { close() },
                    modifier = MobileIconButtonStyle.toModifier().ariaLabel("メニューを閉じる"),
                    ref = ref { it.focus() },
                ) {
                    Box(Modifier.size(32.px), contentAlignment = Alignment.Center) {
                        Image("/img/close.svg", "", Modifier.size(19.px))
                    }
                }
            }
            Column(
                MobileMenuContentStyle.toModifier(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Column(MobilePrimaryMenuStyle.toModifier(), horizontalAlignment = Alignment.CenterHorizontally) {
                    TextButton("Home", "/", language = SiteButtonLanguage.EN)
                    TextButton("Timetable", "/timetable", language = SiteButtonLanguage.EN)
                    TextButton("Sponsors", SiteLinks.Sponsors, language = SiteButtonLanguage.EN)
                    SiteButton(
                        "Ticket",
                        href = "/ticket",
                        size = SiteButtonSize.LARGE,
                        language = SiteButtonLanguage.EN,
                    )
                }
                Box(Modifier.fillMaxWidth().height(1.px).backgroundColor(SitePalette.brand.lightGray))
                Row(MobilePolicyMenuStyle.toModifier()) {
                    TextButton("行動規範", "/code-of-conduct")
                    TextButton("プライバシーポリシー", "/privacy-policy")
                    TextButton("ロゴの使用について", "/brand-logo-guidelines")
                    TextButton("お問い合わせ", "/contact")
                }
            }
        }
    }
}
